import { TAsset, TAssetArea, TTreeBranch } from 'src/typings/configuration.types';
import {
  TChildrenPayload,
  TConfigurationManagementCentralProps,
} from 'src/components/ConfigurationManagementCentral';
import {
  addAssets,
  appendBranch,
  removeAssetByUuid,
  resetSimulationResults,
  setActiveConfigurationUuid,
  setDescription,
  setLocationVisible,
  setName,
  setProjectUuid,
  setRootAsset,
  setSelectedAssetUuid,
  setSettingsData,
  updateAssetTypeForUuid,
  updateAssetValuesForUuid,
} from 'src/redux/configuration/configuration.slice';
import { batch, useSelector } from 'react-redux';
import { configType, isLibrary } from 'src/mocks/configurationSettings';
import {
  getAllFieldTemplatesWithValuesForAsset,
  getAssetValues,
} from 'src/utils/assetsFields/fieldTemplatesWithValues';
import {
  initialApplicationState,
  setIsCustomHouseAddingContinuation,
  setModalCommunitySummaryView,
  setModalHouseSummaryFormVariant,
  setModalHouseSummaryView,
} from 'src/redux/application/application.slice';
import { prepareAreaInput, prepareSettingsInput } from 'src/utils/mutationsGates';
import {
  selectActiveConfigurationUuid,
  selectAssets,
  selectAssetsAmountBelowCommunity,
  selectAssetsTreeRelations,
  selectAssetsValues,
  selectCommunityAsset,
  selectConfigurationCenter,
  selectConfigurationCharacteristic,
  selectIsCommunityCreatedInDB,
  selectRootAssetUuid,
  selectSettingsData,
  selectUsedAssetsNames,
} from 'src/redux/configuration/configuration.selectors';
import {
  selectIsCustomHouseAddingContinuation,
  selectModalHouseSummaryView,
} from 'src/redux/application/application.selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCreateConfigurationMutation,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useSetGlobalSimulationLocationMutation,
  useUpdateConfigurationMutation,
  useUpdateProjectMutation,
} from 'src/graphql';

import { EFormVariant } from 'src/typings/base-types';
import { EModalAssetsManagerView } from 'src/components/ModalAssetsManager';
import { EPredefinedModalIds } from 'src/constants/modals';
import { GRID_MARKET_ASSET_TYPES } from 'src/constants/application';
import { cacheUpdate } from 'src/utils/cacheUpdate';
import { destructureConfigTree } from 'src/utils/configuration/destructureConfigTree';
import { openModal } from 'src/redux/modals/modals.slice';
import { selectIsLoggedIn } from 'src/redux/auth/auth.selectors';
import { selectSelectedLocation } from 'src/redux/map/map.selectors';
import { updateSelectedLocation } from 'src/redux/map/map.slice';
import { useAppDispatch } from 'src/redux/store';
import { useConfigurationUtils } from 'src/hooks/useConfigurationUtils';
import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import { v4 } from 'uuid';

export function ConfigurationManagementCentral({
  children,
}: TConfigurationManagementCentralProps): ReturnType<
  TConfigurationManagementCentralProps['children']
> {
  const dispatch = useAppDispatch();
  const assets = useSelector(selectAssets);
  const communityAsset = useSelector(selectCommunityAsset);
  const isConfigEmpty = Object.keys(assets).length === 0;
  const assetsTreeRelations = useSelector(selectAssetsTreeRelations);
  const assetsAmountBelowCommunity = useSelector(selectAssetsAmountBelowCommunity);
  // const { syncConfigurationWithBE } = useSyncConfigurationWithBE();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const settingsData = useSelector(selectSettingsData);
  const configurationCharacteristic = useSelector(selectConfigurationCharacteristic);
  const rootAssetUuid = useSelector(selectRootAssetUuid);
  const activeConfigurationUuid = useSelector(selectActiveConfigurationUuid);
  const assetsValues = useSelector(selectAssetsValues);
  const isCommunityCreatedInDB = useSelector(selectIsCommunityCreatedInDB);
  const configurationCenter = useSelector(selectConfigurationCenter);
  const usedAssetsNames = useSelector(selectUsedAssetsNames);
  const isCustomHouseAddingContinuation = useSelector(selectIsCustomHouseAddingContinuation);
  const modalHouseSummaryView = useSelector(selectModalHouseSummaryView);
  const selectedLocation = useSelector(selectSelectedLocation);
  const { discardCurrentConfiguration, zoomIntoConfiguration } = useConfigurationUtils();

  const [doSyncWithBackend, setDoSyncWithBackend] = useState<boolean>(false);
  const [doZoomIntoConfiguration, setDoZoomIntoConfiguration] = useState<boolean>(false);

  // Mutations
  const [createConfiguration] = useCreateConfigurationMutation({ errorPolicy: 'all' });
  const [updateConfiguration] = useUpdateConfigurationMutation({ errorPolicy: 'all' });
  const [createProject] = useCreateProjectMutation({ errorPolicy: 'all' });
  const [updateProject] = useUpdateProjectMutation({ errorPolicy: 'all' });
  const [deleteProject] = useDeleteProjectMutation({ errorPolicy: 'all' });
  const [setGlobalSimulationLocation] = useSetGlobalSimulationLocationMutation({
    errorPolicy: 'all',
  });

  const centreCoordinates = useMemo(() => {
    return {
      longitude: configurationCenter?.lng,
      latitude: configurationCenter?.lat,
    };
  }, [configurationCenter?.lat, configurationCenter?.lng]);

  const handleCommunityRemove = useCallback(
    async ({
      projectUuid,
    }: Parameters<NonNullable<TChildrenPayload['handleCommunityRemove']>>[0]) => {
      await deleteProject({
        variables: { projectUuid },
        update(cache) {
          cacheUpdate.deleteProject(cache, { deletedProjectUuid: projectUuid });
        },
      });

      if (configurationCharacteristic.projectUuid === projectUuid) {
        discardCurrentConfiguration({ zoomOut: true });
      }
    },
    [configurationCharacteristic.projectUuid, deleteProject, discardCurrentConfiguration],
  );

  const handleCreateNewCommunity = useCallback(async () => {
    try {
      const { locationVisible, name, description } = configurationCharacteristic;

      const { data: dataA, errors: errorsA } = await createProject({
        variables: {
          locationVisible,
          private: !locationVisible,
          centreCoordinates,
          name,
          description,
        },
      });

      const projectUuid = dataA?.createProject?.uuid;
      if (errorsA) throw errorsA[0].message;

      if (!projectUuid) {
        throw '"projectUuid" is required!';
      }

      const areaInput = prepareAreaInput({
        rootAssetUuid,
        assets,
        assetsTreeRelations,
        assetsValues,
        settingsData,
        configurationCharacteristic,
      });

      const { data: dataB, errors: errorsB } = await createConfiguration({
        variables: {
          name,
          description,
          projectUuid,
          settingsData: prepareSettingsInput(settingsData),
          scenarioData: {
            representation: areaInput,
          },
        },
        update(cache, results) {
          cacheUpdate.createConfiguration(cache, results, {
            latitude: centreCoordinates.latitude,
            longitude: centreCoordinates.longitude,
            locationVisible: locationVisible,
          });
        },
      });

      if (errorsB) {
        handleCommunityRemove({ projectUuid });
        throw errorsB[0].message;
      }

      const configurationUuid = dataB?.createConfiguration?.uuid;

      if (!configurationUuid) {
        throw '"configurationUuid" is required!';
      }

      batch(() => {
        dispatch(setProjectUuid(projectUuid));
        dispatch(setActiveConfigurationUuid(configurationUuid));
      });
    } catch (errors) {
      // TODO: Display error on a toast
      console.error(errors);
    }
  }, [
    assets,
    assetsTreeRelations,
    assetsValues,
    centreCoordinates,
    configurationCharacteristic,
    createConfiguration,
    createProject,
    dispatch,
    handleCommunityRemove,
    rootAssetUuid,
    settingsData,
  ]);

  const handleUpdateCommunity = useCallback(async () => {
    if (!activeConfigurationUuid || !configurationCharacteristic.projectUuid) {
      return console.error('"activeConfigurationUuid" and "projectUuid" must be set!');
    }

    const { locationVisible, name, description, projectUuid } = configurationCharacteristic;

    const areaInput = prepareAreaInput({
      rootAssetUuid,
      assets,
      assetsTreeRelations,
      assetsValues,
      settingsData,
      configurationCharacteristic,
    });
    const settingsInput = prepareSettingsInput(settingsData);

    // We don't use Promise.all() because cache update has to fire after all queries passed.
    await updateProject({
      variables: {
        private: !locationVisible,
        locationVisible,
        centreCoordinates,
        name,
        description,
        projectUuid,
      },
    });

    await setGlobalSimulationLocation({
      variables: {
        configUuid: activeConfigurationUuid,
        centreCoordinates,
        // TODO: Backend should refactor their code and remove boundaryId I think.
        boundaryId: JSON.stringify(centreCoordinates),
        visibility: locationVisible,
      },
    });

    await updateConfiguration({
      variables: {
        configurationUuid: activeConfigurationUuid,
        name,
        description,
        projectUuid,
        settingsData: settingsInput,
        scenarioData: {
          representation: areaInput,
        },
      },
      update(cache, result) {
        cacheUpdate.updateConfiguration(cache, result, {
          projectUuid,
          name,
          latitude: centreCoordinates.latitude,
          longitude: centreCoordinates.longitude,
          locationVisible,
        });
      },
    });
  }, [
    activeConfigurationUuid,
    configurationCharacteristic,
    rootAssetUuid,
    assets,
    assetsTreeRelations,
    assetsValues,
    settingsData,
    updateProject,
    centreCoordinates,
    setGlobalSimulationLocation,
    updateConfiguration,
  ]);

  const currentRefs = useCurrentRefs({
    isCommunityCreatedInDB,
    assetsAmountBelowCommunity,
    handleCreateNewCommunity,
    handleUpdateCommunity,
    zoomIntoConfiguration,
    dispatch,
  });

  const handleAddNewAsset = useCallback(
    (
      {
        values,
        chosenAsset,
        addUnderUuid,
      }: Parameters<TChildrenPayload['handleAddNewAsset']>[0] & { addUnderUuid?: string },
      options?: { stayOnSetSelectedAssetUuid: boolean },
    ) => {
      const { stayOnSetSelectedAssetUuid } = options || {};
      const communityAssetUuid = communityAsset?.uuid || v4();
      const connectorAssetUuid = addUnderUuid || communityAssetUuid;
      let savingTreeBranch: TTreeBranch;

      if (!chosenAsset.isCustomHouse) {
        const destructuredTree = destructureConfigTree(
          chosenAsset.scenarioData?.representation?.serialized,
          {
            assignNewUuids: true,
            parentUuid: connectorAssetUuid,
            assignGeoTagLocation: values.geoTagLocation,
            considerUsedAssetsNames: usedAssetsNames,
          },
        );
        destructuredTree.assetsValues[destructuredTree.rootAssetUuid as string] = values;

        savingTreeBranch = {
          ...destructuredTree,
          rootAssetUuid: destructuredTree.rootAssetUuid as string,
        };
      } else {
        const customHouseUuid = v4();

        savingTreeBranch = {
          assets: {
            [customHouseUuid]: {
              parentUuid: connectorAssetUuid,
              type: chosenAsset.type,
              uuid: customHouseUuid,
            },
          },
          assetsTreeRelations: { [customHouseUuid]: [] },
          rootAssetUuid: customHouseUuid,
          assetsValues: { [customHouseUuid]: values },
        };
      }

      const gridMarketInTreeBranch = Object.values(savingTreeBranch.assets).find((asset) =>
        GRID_MARKET_ASSET_TYPES.includes(asset.type),
      );
      const addingSingleDevice =
        savingTreeBranch.assets[savingTreeBranch.rootAssetUuid].type !== 'Area';

      if (isConfigEmpty) {
        // Add a top-level "Grid", a "Grid Market", a "Community" market and the currently saving asset.
        const gridAsset: TAssetArea = {
          type: 'Area',
          uuid: v4(),
          parentUuid: undefined,
        };
        const gridAssetValues = getAssetValues(
          getAllFieldTemplatesWithValuesForAsset({
            type: 'Area',
            settingsData: settingsData,
            isLibrary,
            configType,
            values: { name: 'Grid' },
            configurationCharacteristic,
          }),
        );

        const gridMarketAsset: TAsset = {
          type: 'InfiniteBus',
          uuid: v4(),
          parentUuid: gridAsset.uuid,
        };
        const gridMarketAssetValues = getAssetValues(
          getAllFieldTemplatesWithValuesForAsset({
            type: 'InfiniteBus',
            settingsData: settingsData,
            isLibrary,
            configType,
            configurationCharacteristic,
          }),
        );

        const communityAsset: TAsset = {
          type: 'Area',
          uuid: communityAssetUuid,
          parentUuid: gridAsset.uuid,
        };
        const communityAssetValues = getAssetValues(
          getAllFieldTemplatesWithValuesForAsset({
            type: 'Area',
            settingsData: settingsData,
            isLibrary,
            configType,
            configurationCharacteristic,
            values: { name: 'Community' },
          }),
        );

        batch(() => {
          dispatch(setRootAsset(gridAsset));
          dispatch(addAssets([gridMarketAsset, communityAsset]));
          dispatch(
            updateAssetValuesForUuid([
              {
                values: gridAssetValues,
                uuid: gridAsset.uuid,
              },
              {
                values: gridMarketAssetValues,
                uuid: gridMarketAsset.uuid,
              },
              {
                values: communityAssetValues,
                uuid: communityAsset.uuid,
              },
            ]),
          );
          dispatch(appendBranch(savingTreeBranch));

          if (gridMarketInTreeBranch) {
            dispatch(removeAssetByUuid(gridMarketInTreeBranch.uuid));
          }

          if (!stayOnSetSelectedAssetUuid) {
            dispatch(
              setSelectedAssetUuid(
                addingSingleDevice ? communityAsset.uuid : savingTreeBranch.rootAssetUuid,
              ),
            );
          }

          setDoZoomIntoConfiguration(true);
        });
      } else {
        batch(() => {
          if (!stayOnSetSelectedAssetUuid) {
            dispatch(
              setSelectedAssetUuid(
                addingSingleDevice ? communityAsset?.uuid : savingTreeBranch.rootAssetUuid,
              ),
            );
          }

          dispatch(appendBranch(savingTreeBranch));

          if (gridMarketInTreeBranch) {
            dispatch(removeAssetByUuid(gridMarketInTreeBranch.uuid));
          }
        });
      }

      dispatch(updateSelectedLocation(null));

      if (isCommunityCreatedInDB) {
        setDoSyncWithBackend(true);
      }

      return {
        addedAssetUuid: savingTreeBranch.rootAssetUuid,
      };
    },
    [
      communityAsset?.uuid,
      usedAssetsNames,
      isConfigEmpty,
      isCommunityCreatedInDB,
      settingsData,
      configurationCharacteristic,
      dispatch,
    ],
  );

  const handleAddAssetUnderUuid = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAddAssetUnderUuid']>>[0]) => {
      handleAddNewAsset(payload, { stayOnSetSelectedAssetUuid: true });
    },
    [handleAddNewAsset],
  );

  const handleAddNewCustomHouse = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAddNewCustomHouse']>>[0]) => {
      const { addedAssetUuid } = handleAddNewAsset(payload, { stayOnSetSelectedAssetUuid: true });

      batch(() => {
        dispatch(setModalHouseSummaryView(payload.nextView));
        if (payload.nextView === EModalAssetsManagerView.Form) {
          dispatch(setModalHouseSummaryFormVariant(EFormVariant.Advanced));
          dispatch(setIsCustomHouseAddingContinuation(true));
        }
      });

      // Important! setSelectedAssetUuid after setModalHouseSummaryView
      dispatch(setSelectedAssetUuid(addedAssetUuid));
    },
    [dispatch, handleAddNewAsset],
  );

  const handleAssetValuesSave = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAssetValuesSave']>>[0]) => {
      dispatch(updateAssetValuesForUuid({ uuid: payload.assetUuid, values: payload.values }));
      if (payload.assetType) {
        dispatch(updateAssetTypeForUuid({ uuid: payload.assetUuid, assetType: payload.assetType }));
      }

      if (isCommunityCreatedInDB) {
        setDoSyncWithBackend(true);
      }
    },
    [dispatch, isCommunityCreatedInDB],
  );

  const handleAssetRemove = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleAssetRemove']>>[0]) => {
      dispatch(removeAssetByUuid(payload.assetUuid));

      if (isCommunityCreatedInDB) {
        setDoSyncWithBackend(true);
      }
    },
    [dispatch, isCommunityCreatedInDB],
  );

  const handleAssetDuplicate = useCallback(
    ({ assetUuid }: Parameters<NonNullable<TChildrenPayload['handleAssetDuplicate']>>[0]) => {
      // console.log('🌴 TODO: Duplicate: ', assetUuid);
    },
    [],
  );

  const handleSettingsDataSave = useCallback(
    (payload: Parameters<NonNullable<TChildrenPayload['handleSettingsDataSave']>>[0]) => {
      batch(() => {
        dispatch(setName(payload.name));
        dispatch(setDescription(payload.description));
        dispatch(setLocationVisible(payload.locationVisible));
        dispatch(setSettingsData(payload.settingsData));

        if (communityAsset?.uuid) {
          dispatch(
            updateAssetValuesForUuid({
              values: {
                ...assetsValues[communityAsset.uuid],
                gridFeeConstant: payload.communityAssetSettings.gridFeeConstant,
                gridFeePercentage: payload.communityAssetSettings.gridFeePercentage,
              },
              uuid: communityAsset.uuid,
            }),
          );
        }
      });

      setDoSyncWithBackend(true);
    },
    [assetsValues, communityAsset?.uuid, dispatch],
  );

  /*
   *
   *
   * Sync With Backend
   *
   */
  useEffect(
    function syncWithBackend() {
      (async () => {
        if (!doSyncWithBackend) return;

        if (!isLoggedIn) {
          if (
            currentRefs.current.assetsAmountBelowCommunity === 2 ||
            currentRefs.current.assetsAmountBelowCommunity === 50
          ) {
            currentRefs.current.dispatch(openModal(EPredefinedModalIds.MODAL_AUTH_REGISTER));
          }
          return;
        }

        if (!currentRefs.current.isCommunityCreatedInDB) {
          await currentRefs.current.handleCreateNewCommunity();
          setDoSyncWithBackend(false);
        } else {
          await currentRefs.current.handleUpdateCommunity();
          setDoSyncWithBackend(false);
        }

        currentRefs.current.dispatch(resetSimulationResults());
      })();
    },
    [assetsValues, assets, settingsData, isLoggedIn, doSyncWithBackend, currentRefs],
  );

  /*
   *
   *
   * Zoom into newly added asset (configuration)
   *
   */
  useEffect(() => {
    if (!doZoomIntoConfiguration) return;

    currentRefs.current.zoomIntoConfiguration({ assetsValues });
    setDoZoomIntoConfiguration(false);
  }, [assetsValues, assets, settingsData, doZoomIntoConfiguration, currentRefs]);

  useEffect(() => {
    if (
      assetsAmountBelowCommunity === 2 &&
      !isCommunityCreatedInDB &&
      modalHouseSummaryView === initialApplicationState.modalHouseSummaryView
    ) {
      dispatch(setSelectedAssetUuid(communityAsset?.uuid));
      dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.Form));
    }
  }, [
    assetsAmountBelowCommunity,
    communityAsset?.uuid,
    dispatch,
    isCommunityCreatedInDB,
    isCustomHouseAddingContinuation,
    modalHouseSummaryView,
  ]);

  useEffect(() => {
    if (selectedLocation) {
      dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.SaveOrSettings));
    }
  }, [dispatch, selectedLocation]);

  // useEffect(() => {
  //   if (selectedLocation) {
  //     dispatch(setModalCommunitySummaryView(EModalAssetsManagerView.AddHouse));
  //   }
  // }, [dispatch, selectedLocation]);

  return children({
    handleAddNewAsset,
    handleAddNewCustomHouse,
    handleAddAssetUnderUuid,
    handleAssetValuesSave,
    handleSettingsDataSave,
    handleAssetRemove,
    handleCommunityRemove,
    handleAssetDuplicate,
  });
}
